import React, { useEffect, useState } from "react";
import { exportOrder } from "@/lib/exportOrder";
import { api } from "@/lib/api";
import { useTableSort } from "@/lib/tableSort";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Building2, Users2, Landmark, CheckCircle2, Target, Flag, PhoneCall, Download, FileText, AlertTriangle, Trophy, Loader2, CalendarClock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ElectionCountdown } from "@/components/ElectionCountdown";
import { BranchVoters } from "@/components/BranchVoters";
import { BRANCH_ELECTION_DATES, branchElectionLabel, branchDaysLeft } from "@/lib/constants";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";

const NAVY = "#0F2C59";
const GREEN = "#16A34A";
const AMBER = "#D97706";

function HealthBadge({ score }) {
  const c = score >= 70 ? GREEN : score >= 45 ? AMBER : "#DC2626";
  const label = score >= 70 ? "İyi" : score >= 45 ? "Orta" : "Riskli";
  return (
    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: `${c}1a`, color: c }} data-testid="panel-health-badge">
      Sağlık {score} · {label}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg grid place-items-center shrink-0" style={{ background: `${color}1a`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-xl font-bold leading-none tabular-nums" style={{ color }}>{value}</div>
        <div className="text-[11px] text-muted-foreground mt-1">{label}</div>
      </div>
    </div>
  );
}

function ScenarioCard({ b }) {
  const [bad, setBad] = useState(false);
  const f = b.forecast_2026;
  if (!f || !f.eligible) return null;
  const s = bad ? f.kotu : f.tahmin;
  const bar = [
    { k: "Bize", v: s.bize, p: s.bize_pct, c: GREEN },
    { k: "Karşı", v: s.karsi, p: s.karsi_pct, c: "#DC2626" },
    { k: "Belirsiz", v: s.other, p: s.other_pct, c: "#94A3B8" },
  ];
  return (
    <div className={`rounded-xl border p-4 space-y-3 ${bad ? "border-red-300 bg-red-50/60 dark:border-red-900 dark:bg-red-950/30" : "border-border"}`}
      data-testid={`branch-scenario-${b.branch}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold" style={{ color: bad ? "#991B1B" : NAVY }}>
            {bad ? "Kötü Senaryo — en kötü ihtimal" : "Seçim Tahmini"}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {bad ? "Bize oy verecek olanların %10'u karşıya geçer, %10'u sandığa gelmez" : f.basis}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ background: s.win ? `${GREEN}1a` : "#DC26261a", color: s.win ? GREEN : "#DC2626" }}
            data-testid={`scenario-result-${b.branch}`}>
            {s.win ? `Kazanıyor (+${s.margin})` : `Kaybediyor (${s.margin})`}
          </span>
          <button type="button" onClick={() => setBad((v) => !v)} data-testid={`scenario-toggle-${b.branch}`}
            className={`text-xs px-2.5 py-1 rounded-md border transition-colors whitespace-nowrap ${bad ? "bg-[#991B1B] text-white border-[#991B1B]" : "border-red-300 text-red-700 hover:bg-red-50 dark:text-red-300 dark:border-red-800 dark:hover:bg-red-950/40"}`}>
            {bad ? "← Tahmine dön" : "⚠ Kötü Senaryo"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={CheckCircle2} label="Bize gelecek oy" value={`${s.bize} · %${s.bize_pct}`} color={GREEN} testid={`scenario-bize-${b.branch}`} />
        <Kpi icon={Flag} label="Karşıya gidecek" value={`${s.karsi} · %${s.karsi_pct}`} color="#DC2626" testid={`scenario-karsi-${b.branch}`} />
        <Kpi icon={Users2} label="Tahmini katılım" value={`${s.turnout} · %${s.participation}`} color={NAVY} testid={`scenario-turnout-${b.branch}`} />
        <Kpi icon={Trophy} label="Kazanmak için" value={`${s.win_target} · %50`} color={AMBER} testid={`scenario-wintarget-${b.branch}`} />
      </div>

      <div style={{ width: "100%", height: 150 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart data={bar} margin={{ top: 14, right: 8, left: -20, bottom: 0 }}>
            <XAxis dataKey="k" fontSize={11} />
            <YAxis allowDecimals={false} fontSize={10} />
            <Tooltip formatter={(v, _n, p) => [`${v} oy · %${p?.payload?.p ?? 0}`, ""]} />
            <Bar dataKey="v" radius={[4, 4, 0, 0]} label={{ position: "top", fontSize: 11, formatter: (v) => v }}>
              {bar.map((x, i) => <Cell key={i} fill={x.c} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-foreground">
        Yüzdeler tahmini katılım ({s.turnout} oy) üzerinden hesaplanır · {f.eligible} FAAL üye · Şube yöneticisinin girdiği tahmin varsa o, yoksa merkezi tahmin esas alınır.
      </p>
    </div>
  );
}


function BranchCard({ b, elSeed, onSavedEl }) {
  const teamT = useTableSort(b.team || []);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    let ok = true;
    api.get("/dashboard/branch-trend", { params: { branch: b.branch } })
      .then(({ data }) => { if (ok) setTrend((data || []).map((s) => ({ ...s, label: (s.date || "").slice(5) }))); })
      .catch(() => {});
    return () => { ok = false; };
  }, [b.branch]);

  const pct = b.target > 0 ? Math.min(Math.round((b.secured / b.target) * 100), 100) : 0;
  const chart = [
    { name: "Kesin", value: b.kesin, color: GREEN },
    { name: "Belki", value: b.belki, color: "#F59E0B" },
    { name: "Gelmeyecek", value: b.gelmeyecek, color: "#64748B" },
    { name: "Ulaşılamadı", value: b.ulasilamadi, color: "#0EA5E9" },
    { name: "Karşı", value: b.karsi, color: "#DC2626" },
    { name: "İşaretsiz", value: b.bos, color: "#CBD5E1" },
  ];
  return (
    <Card className="p-5 space-y-5" data-testid={`branch-panel-${b.branch}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${NAVY}1a`, color: NAVY }}>
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#0F2C59] dark:text-sky-300">{b.branch} Şubesi</h2>
            <p className="text-xs text-muted-foreground">Bağımsız şube seçimi · Hedef {b.target} oy (beklenen katılım %{b.target_pct} → yarısı+1) · {b.faal} FAAL üye</p>
            <p className="text-xs font-medium flex items-center gap-1.5 mt-0.5" data-testid={`branch-election-date-${b.branch}`}>
              <CalendarClock className="h-3.5 w-3.5 text-[#0F2C59] dark:text-sky-300" />
              {BRANCH_ELECTION_DATES[b.branch]
                ? <span className="text-foreground">Seçim: {branchElectionLabel(BRANCH_ELECTION_DATES[b.branch])} · <b>{branchDaysLeft(BRANCH_ELECTION_DATES[b.branch])} gün kaldı</b></span>
                : <span className="text-muted-foreground">Seçim tarihi belirlenmedi</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HealthBadge score={b.health} />
          <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: b.winning ? `${GREEN}1a` : `${AMBER}1a`, color: b.winning ? GREEN : AMBER }}>
            {b.winning ? "Hedefte" : `Hedefe -${b.remaining}`}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Kpi icon={Users2} label="Toplam Üye" value={b.total} color={NAVY} testid={`kpi-total-${b.branch}`} />
        <Kpi icon={Landmark} label="FAAL Üye" value={b.faal} color={NAVY} testid={`kpi-faal-${b.branch}`} />        <Kpi icon={CheckCircle2} label="KESİN / Güvence" value={b.secured} color={GREEN} testid={`kpi-secured-${b.branch}`} />
        <Kpi icon={Target} label="Hedef" value={b.target} color={AMBER} testid={`kpi-target-${b.branch}`} />
        <Kpi icon={Flag} label="Hedefe Kalan" value={b.remaining} color={b.remaining === 0 ? GREEN : "#DC2626"} testid={`kpi-remaining-${b.branch}`} />
        <Kpi icon={PhoneCall} label="İşaretlenen" value={`${b.contacted}`} color="#0EA5E9" testid={`kpi-contacted-${b.branch}`} />
      </div>

      {!!(b.layer_diff && (b.layer_diff.layer_aski || b.layer_diff.layer_faal)) && (
        <div className="text-xs rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-amber-800 dark:text-amber-300 space-y-1" data-testid={`branch-layer-diff-${b.branch}`}>
          <p>
            <b>Neden Seçmen Listesi'nden farklı?</b> Bu panel <b>şube katmanı</b> kayıtlarını kullanır;
            Seçmen Listesi ise merkez kaydını gösterir.
          </p>
          <p>
            Merkezde FAAL görünen <b>{b.layer_diff.central_faal}</b> firmadan{" "}
            <b>{b.layer_diff.layer_aski}</b> tanesi şube katmanında ASKI işaretli olduğu için buradaki
            FAAL sayısına girmiyor
            {b.layer_diff.layer_faal ? <> · şube katmanında FAAL olup merkezde ASKI olan <b>{b.layer_diff.layer_faal}</b> firma ise ekleniyor</> : null}.
            {" "}Net: {b.layer_diff.central_faal} − {b.layer_diff.layer_aski}
            {b.layer_diff.layer_faal ? ` + ${b.layer_diff.layer_faal}` : ""} = <b>{b.faal}</b>
          </p>
          <p>
            Farklı kayıtları görmek için aşağıdaki şube seçmen listesinde{" "}
            <b>“Yalnız durumu farklı olanlar”</b> filtresini kullanın veya{" "}
            <a href="/katman-farklari" className="underline font-medium">Katman Farkları</a> ekranına gidin.
          </p>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between text-xs mb-1.5">
          <span className="text-muted-foreground">Hedefe ilerleme</span>
          <span className="font-semibold" style={{ color: pct >= 100 ? GREEN : NAVY }}>{b.secured} / {b.target} (%{pct})</span>
        </div>
        <div className="h-3 w-full rounded-full bg-muted overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: pct >= 100 ? GREEN : NAVY }} data-testid={`progress-${b.branch}`} />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid={`branch-forecast-${b.branch}`}>
        <span>Hedef temeli: <b className="text-foreground">{b.target_basis}</b></span>
        <span>Garanti (KESİN): <b style={{ color: GREEN }}>{b.forecast?.guaranteed ?? b.secured}</b></span>
        <span>İyimser (+BELKİ): <b style={{ color: AMBER }}>{b.forecast?.optimistic ?? b.secured}</b></span>
        {b.participation_2018 != null && <span>2018 katılımı %{b.participation_2018}</span>}
        {b.participation_2022 != null && <span>2022 katılımı %{b.participation_2022}</span>}
      </div>

      <ScenarioCard b={b} />

      {[b.participation_2018, b.participation_2022, b.participation_2026].filter((x) => x != null).length >= 2 && (
        <div className="rounded-lg border border-border p-3" data-testid={`participation-trend-${b.branch}`}>
          <p className="text-xs font-medium mb-1">Katılım Oranı Trendi (2018 → 2022 → 2026)</p>
          <div style={{ width: "100%", height: 120 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <LineChart data={[{ year: "2018", k: b.participation_2018 }, { year: "2022", k: b.participation_2022 }, { year: "2026*", k: b.participation_2026 }].filter((p) => p.k != null)} margin={{ top: 14, right: 18, left: -14, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="year" fontSize={11} />
                <YAxis domain={[0, 100]} tickFormatter={(v) => `%${v}`} fontSize={10} />
                <Tooltip formatter={(v) => [`%${v}`, "Katılım"]} />
                <Line type="monotone" dataKey="k" name="Katılım" stroke={NAVY} strokeWidth={2} dot={{ r: 4 }} label={{ position: "top", formatter: (v) => `%${v}`, fontSize: 10 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">2026* mevcut tahmine göre: (KESİN+KARŞI) / (KESİN+KARŞI+GELMEYECEK).</p>
        </div>
      )}

      {!b.winning && (
        <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300" data-testid={`branch-warning-${b.branch}`}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span><b>Hedefin altında.</b> Kazanmak için {b.remaining} KESİN oy daha gerekli (hedef {b.target} oy).</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <p className="text-sm font-medium mb-2">FAAL Üyelerde 2026 Tahmin Dağılımı</p>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={chart} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <XAxis dataKey="name" fontSize={10} interval={0} angle={-15} textAnchor="end" height={44} />
                <YAxis allowDecimals={false} fontSize={11} />
                <Tooltip />
                <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                  {chart.map((c, i) => <Cell key={i} fill={c.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <p className="text-sm font-medium mb-2">Günlük KESİN / Hedef Trendi</p>
          <div style={{ width: "100%", height: 220 }} data-testid={`trend-${b.branch}`}>
            {trend.length >= 2 ? (
              <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                <LineChart data={trend} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" fontSize={10} />
                  <YAxis allowDecimals={false} fontSize={11} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="secured" name="KESİN" stroke={GREEN} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="target" name="Hedef" stroke={AMBER} strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-center text-xs text-muted-foreground px-4">
                Günlük trend için yeterli geçmiş veri henüz oluşmadı. Her gün otomatik kaydedilen anlık görüntüler biriktikçe KESİN/hedef ilerlemesi burada çizilecek.
              </div>
            )}
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-medium mb-2">Saha Ekibi İlerlemesi</p>
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm" data-testid={`team-table-${b.branch}`}>
            <thead>
              <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <teamT.Th sortKey="bt-sorumlu" get={(r) => r.name} text className="px-3 py-2 font-medium">Sorumlu</teamT.Th>
                <teamT.Th sortKey="bt-atanan" get={(r) => r.assigned} className="px-3 py-2 font-medium text-right">Atanan</teamT.Th>
                <teamT.Th sortKey="bt-arandi" get={(r) => r.contacted} className="px-3 py-2 font-medium text-right">Arandı</teamT.Th>
                <teamT.Th sortKey="bt-kesin" get={(r) => r.kesin} className="px-3 py-2 font-medium text-right">Kesin</teamT.Th>
              </tr>
            </thead>
            <tbody>
              {teamT.sorted.map((t) => (
                <tr key={t.name} className="border-t border-border/60">
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{t.assigned}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{t.contacted}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: GREEN }}>{t.kesin}</td>
                </tr>
              ))}
              {(!b.team || b.team.length === 0) && (
                <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Atama yok</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <BranchElections branch={b.branch} seed={elSeed} onSaved={onSavedEl} />
    </Card>
  );
}

function BranchCountCheck({ branches }) {
  const rows = branches.map((b) => {
    const ld = b.layer_diff || { central_faal: 0, layer_aski: 0, layer_faal: 0 };
    return {
      name: b.branch, total: b.total, central: ld.central_faal, panel: b.faal,
      diff: b.faal - ld.central_faal, aski: ld.layer_aski, extra: ld.layer_faal,
    };
  });
  const bad = rows.filter((r) => r.diff !== 0);
  const gap = bad.reduce((s, r) => s + Math.abs(r.diff), 0);
  const ordered = [...rows].sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff) || a.name.localeCompare(b.name, "tr"));
  const ccT = useTableSort(ordered);
  return (
    <Card className="p-5 space-y-4" data-testid="branch-count-check">
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: `${NAVY}1a`, color: NAVY }}>
          <Building2 className="h-5 w-5" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-[#0F2C59] dark:text-sky-300">Şube ↔ Merkez Sayı Kontrolü</h2>
          <p className="text-xs text-muted-foreground">
            Merkez seçmen listesindeki FAAL sayısı ile şube panelinde görünen FAAL sayısını yan yana gösterir. Bu ekran yalnız okur, veri değiştirmez.
          </p>
        </div>
        <span className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-full"
          style={bad.length === 0
            ? { background: `${GREEN}1a`, color: GREEN }
            : { background: "#DC26261a", color: "#DC2626" }}
          data-testid="branch-count-check-summary">
          {bad.length === 0
            ? `${rows.length} şubede fark yok`
            : `${rows.length - bad.length} şube eşit · ${bad.length} şubede toplam ${gap} firma farkı`}
        </span>
      </div>
      <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="branch-count-check-table">
          <thead>
            <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
              <ccT.Th sortKey="cc-sube" get={(r) => r.name} text className="px-3 py-2 font-medium">Şube</ccT.Th>
              <ccT.Th sortKey="cc-total" get={(r) => r.total} className="px-3 py-2 font-medium text-right">Toplam</ccT.Th>
              <ccT.Th sortKey="cc-merkez" get={(r) => r.central} className="px-3 py-2 font-medium text-right">Merkez FAAL</ccT.Th>
              <ccT.Th sortKey="cc-panel" get={(r) => r.panel} className="px-3 py-2 font-medium text-right">Şube panelinde FAAL</ccT.Th>
              <ccT.Th sortKey="cc-fark" get={(r) => Math.abs(r.diff)} lowBest className="px-3 py-2 font-medium text-right">Fark</ccT.Th>
              <ccT.Th sortKey="cc-aski" get={(r) => r.aski} lowBest className="px-3 py-2 font-medium text-right">Şubede ASKI</ccT.Th>
              <ccT.Th sortKey="cc-ek" get={(r) => r.extra} className="px-3 py-2 font-medium text-right">Şubede FAAL (merkezde askı)</ccT.Th>
              <ccT.Th sortKey="cc-durum" get={(r) => (r.diff === 0 ? 1 : 0)} className="px-3 py-2 font-medium">Durum</ccT.Th>
            </tr>
          </thead>
          <tbody>
            {ccT.sorted.map((r) => (
              <tr key={r.name} className={`border-t border-border/60 ${r.diff !== 0 ? "bg-amber-50/70 dark:bg-amber-950/20" : ""}`}
                data-testid={`branch-count-row-${r.name}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.total}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.central}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">{r.panel}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold"
                  style={{ color: r.diff === 0 ? GREEN : "#DC2626" }}>
                  {r.diff === 0 ? "0" : r.diff > 0 ? `+${r.diff}` : r.diff}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.aski || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.extra || "—"}</td>
                <td className="px-3 py-2">
                  {r.diff === 0
                    ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${GREEN}1a`, color: GREEN }}>Eşit</span>
                    : <a href="/katman-farklari" className="text-xs font-semibold px-2 py-0.5 rounded-full underline"
                      style={{ background: "#DC26261a", color: "#DC2626" }}>Fark var · incele</a>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function BranchCompare({ branches }) {
  const rows = [...branches].map((b) => ({
    name: b.branch, faal: b.faal, kesin: b.secured, target: b.target, remaining: b.remaining,
    health: b.health, winning: b.winning,
    pct: b.target > 0 ? Math.min(Math.round((b.secured / b.target) * 100), 100) : 0,
  })).sort((a, b) => b.pct - a.pct);
  const cmpT = useTableSort(rows);
  return (
    <Card className="p-5 space-y-4" data-testid="branch-compare">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: `${NAVY}1a`, color: NAVY }}><Trophy className="h-5 w-5" /></div>
        <div>
          <h2 className="text-lg font-bold text-[#0F2C59] dark:text-sky-300">Şube Karşılaştırma</h2>
          <p className="text-xs text-muted-foreground">Hedefe yakınlık (%) — hangi şube kazanmaya en yakın</p>
        </div>
      </div>
      <div style={{ width: "100%", height: Math.max(rows.length * 38 + 24, 120) }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart layout="vertical" data={rows} margin={{ top: 4, right: 44, left: 8, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
            <XAxis type="number" domain={[0, 100]} fontSize={11} tickFormatter={(v) => `%${v}`} />
            <YAxis type="category" dataKey="name" width={120} fontSize={11} />
            <Tooltip formatter={(v) => [`%${v}`, "Hedefe yakınlık"]} />
            <Bar dataKey="pct" radius={[0, 4, 4, 0]} label={{ position: "right", formatter: (v) => `%${v}`, fontSize: 10 }}>
              {rows.map((r, i) => <Cell key={i} fill={r.winning ? GREEN : r.pct >= 75 ? AMBER : "#DC2626"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="branch-compare-table">
          <thead>
            <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
              <cmpT.Th sortKey="bc-sube" get={(r) => r.name} text className="px-3 py-2 font-medium">Şube</cmpT.Th>
              <cmpT.Th sortKey="bc-faal" get={(r) => r.faal} className="px-3 py-2 font-medium text-right">FAAL</cmpT.Th>
              <cmpT.Th sortKey="bc-kesin" get={(r) => r.kesin} className="px-3 py-2 font-medium text-right">KESİN</cmpT.Th>
              <cmpT.Th sortKey="bc-hedef" get={(r) => r.target} className="px-3 py-2 font-medium text-right">Hedef</cmpT.Th>
              <cmpT.Th sortKey="bc-kalan" get={(r) => r.remaining} lowBest className="px-3 py-2 font-medium text-right">Kalan</cmpT.Th>
              <cmpT.Th sortKey="bc-yakinlik" get={(r) => r.pct} className="px-3 py-2 font-medium text-right">Yakınlık</cmpT.Th>
              <cmpT.Th sortKey="bc-saglik" get={(r) => r.health} className="px-3 py-2 font-medium text-right">Sağlık</cmpT.Th>
              <cmpT.Th sortKey="bc-durum" get={(r) => (r.winning ? 1 : 0)} className="px-3 py-2 font-medium">Durum</cmpT.Th>
            </tr>
          </thead>
          <tbody>
            {cmpT.sorted.map((r) => (
              <tr key={r.name} className={`border-t border-border/60 ${!r.winning ? "bg-red-50/60 dark:bg-red-950/20" : ""}`}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.faal}</td>
                <td className="px-3 py-2 text-right tabular-nums" style={{ color: GREEN }}>{r.kesin}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.target}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold" style={{ color: r.remaining === 0 ? GREEN : "#DC2626" }}>{r.remaining}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold">%{r.pct}</td>
                <td className="px-3 py-2 text-right tabular-nums">{r.health}</td>
                <td className="px-3 py-2">
                  {r.winning
                    ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${GREEN}1a`, color: GREEN }}>Hedefte</span>
                    : <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: "#DC26261a", color: "#DC2626" }}>-{r.remaining} oy</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function BranchElections({ branch, seed, onSaved }) {
  const empty = { biz: "", karsi: "", gelmedi: "" };
  const [y18, setY18] = useState(empty);
  const [y22, setY22] = useState(empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const s = seed || {};
    setY18({ biz: s["2018"]?.biz ?? "", karsi: s["2018"]?.karsi ?? "", gelmedi: s["2018"]?.gelmedi ?? "" });
    setY22({ biz: s["2022"]?.biz ?? "", karsi: s["2022"]?.karsi ?? "", gelmedi: s["2022"]?.gelmedi ?? "" });
  }, [seed, branch]);
  const num = (v) => parseInt(v || 0, 10) || 0;
  const save = async () => {
    setSaving(true);
    try {
      const elections = {};
      const a = { biz: num(y18.biz), karsi: num(y18.karsi), gelmedi: num(y18.gelmedi) };
      const c = { biz: num(y22.biz), karsi: num(y22.karsi), gelmedi: num(y22.gelmedi) };
      if (a.biz + a.karsi + a.gelmedi > 0) elections["2018"] = a;
      if (c.biz + c.karsi + c.gelmedi > 0) elections["2022"] = c;
      const { data } = await api.patch("/dashboard/branch-elections", { branch, elections });
      toast.success(`${branch} 2018/2022 sonuçları kaydedildi`);
      onSaved?.(branch, data.elections);
    } catch { toast.error("Kaydedilemedi"); }
    finally { setSaving(false); }
  };
  const row = (label, val, setVal, testid) => (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-12 shrink-0">{label}</span>
      {[["biz", "Bize"], ["karsi", "Karşı"], ["gelmedi", "Gelmeyen"]].map(([k, l]) => (
        <Input key={k} value={val[k]} onChange={(e) => setVal({ ...val, [k]: e.target.value.replace(/[^\d]/g, "") })}
          placeholder={l} inputMode="numeric" className="h-8 text-xs text-center flex-1" data-testid={`${testid}-${k}`} />
      ))}
    </div>
  );
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`branch-elections-${branch}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">Geçmiş Seçim Sonuçları (2018 / 2022)</p>
        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={saving} onClick={save} data-testid={`branch-elections-save-${branch}`}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
        </Button>
      </div>
      {row("2018", y18, setY18, `bel-2018-${branch}`)}
      {row("2022", y22, setY22, `bel-2022-${branch}`)}
      <p className="text-[11px] text-muted-foreground">Bu şubeye özeldir; merkez komite verilerini etkilemez.</p>
    </div>
  );
}

function BranchConflicts({ branch }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const cfT = useTableSort(d?.items || []);
  useEffect(() => {
    let ok = true; setLoading(true);
    api.get("/dashboard/branch-conflicts", { params: { branch: branch && branch !== "all" ? branch : undefined } })
      .then(({ data }) => { if (ok) setD(data); })
      .catch(() => { if (ok) setD(null); })
      .finally(() => { if (ok) setLoading(false); });
    return () => { ok = false; };
  }, [branch]);
  if (loading) return <Skeleton className="h-40 w-full" />;
  const items = d?.items || [];
  const PLAB = { KESIN: "Kesin", BELKI: "Belki", GELMEYECEK: "Gelmeyecek", ULASILAMADI: "Ulaşılamadı", KARSI: "Karşı", "": "İşaretsiz" };
  return (
    <Card className="p-5 space-y-4 border-amber-300 dark:border-amber-900" data-testid="branch-conflicts">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg grid place-items-center" style={{ background: "#DC26261a", color: "#DC2626" }}><AlertTriangle className="h-5 w-5" /></div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-[#0F2C59] dark:text-sky-300">Merkezde Bizde · Şubede Kaçan Firmalar</h2>
          <p className="text-xs text-muted-foreground">Merkezde güvencemizde (KESİN / Yetki Belgesi) olup şubede henüz kesinleşmemiş firmalar — en riskliler (Karşı) üstte</p>
        </div>
        <span className="ml-auto text-sm font-bold px-3 py-1 rounded-full shrink-0" style={{ background: "#DC26261a", color: "#DC2626" }} data-testid="branch-conflicts-count">{d?.total ?? 0}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-muted-foreground text-center py-6" data-testid="branch-conflicts-empty">
          Çelişki bulunamadı — merkezde güvenceli firmalar şubede de kaçmıyor.
        </div>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
          <table className="w-full text-sm" data-testid="branch-conflicts-table">
            <thead>
              <tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <cfT.Th sortKey="cf-firma" get={(r) => r.company_name} text className="px-3 py-2 font-medium">Firma</cfT.Th>
                <cfT.Th sortKey="cf-sube" get={(r) => r.branch} text className="px-3 py-2 font-medium">Şube</cfT.Th>
                <cfT.Th sortKey="cf-merkez" get={(r) => (r.center_proxy && r.center_prediction !== "KESIN" ? "Yetki Belgesi" : "Kesin")} text className="px-3 py-2 font-medium">Merkez</cfT.Th>
                <cfT.Th sortKey="cf-egilim" get={(r) => r.branch_prediction || ""} text className="px-3 py-2 font-medium">Şube Eğilimi</cfT.Th>
                <cfT.Th sortKey="cf-sorumlu" get={(r) => r.branch_responsible} text className="px-3 py-2 font-medium">Şube Sorumlusu</cfT.Th>
              </tr>
            </thead>
            <tbody>
              {cfT.sorted.map((r) => (
                <tr key={r.id} className="border-t border-border/60" data-testid={`conflict-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium">{r.company_name}<span className="block text-[11px] font-normal text-muted-foreground">Komite #{r.committee_no}</span></td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.branch}</td>
                  <td className="px-3 py-2"><span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${GREEN}1a`, color: GREEN }}>{r.center_proxy && r.center_prediction !== "KESIN" ? "Yetki Belgesi" : "Kesin"}</span></td>
                  <td className="px-3 py-2"><span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: r.branch_prediction === "KARSI" ? "#DC26261a" : "#D977061a", color: r.branch_prediction === "KARSI" ? "#DC2626" : "#D97706" }}>{PLAB[r.branch_prediction] ?? (r.branch_prediction || "İşaretsiz")}</span></td>
                  <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.branch_responsible || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function BranchDetail({ b }) {
  const dtT = useTableSort(b.team || []);
  const predData = [
    { k: "Kesin", v: b.kesin, c: "#16A34A" },
    { k: "Belki", v: b.belki, c: "#F59E0B" },
    { k: "Gelmeyecek", v: b.gelmeyecek, c: "#6B7280" },
    { k: "Ulaşılamadı", v: b.ulasilamadi, c: "#9CA3AF" },
    { k: "Karşı", v: b.karsi, c: "#DC2626" },
    { k: "İşaretsiz", v: b.bos, c: "#CBD5E1" },
  ];
  const targetData = [
    { k: "Hedef", v: b.target, c: NAVY },
    { k: "Kesin (Garanti)", v: b.forecast?.guaranteed ?? b.secured, c: "#16A34A" },
    { k: "İyimser (+Belki)", v: b.forecast?.optimistic ?? b.secured, c: "#0EA5E9" },
  ];
  const partData = [
    { k: "2018", v: b.participation_2018 ?? 0 },
    { k: "2022", v: b.participation_2022 ?? 0 },
    { k: "2026", v: b.participation_2026 ?? 0 },
  ];
  const kpi = [
    { label: "Toplam Üye", val: b.total, color: NAVY },
    { label: "FAAL", val: b.faal, color: "#16A34A" },
    { label: "Hedef (katılımın yarısı+1)", val: b.target, color: "#0EA5E9" },
    { label: "Kesin Oy", val: b.secured, color: "#16A34A" },
    { label: "Kalan", val: b.remaining, color: b.remaining > 0 ? "#DC2626" : "#16A34A" },
    { label: "Sağlık", val: `%${b.health}`, color: b.health >= 80 ? "#16A34A" : b.health >= 50 ? "#F59E0B" : "#DC2626" },
  ];
  return (
    <Card className="p-5 space-y-6" data-testid="branch-detail">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${NAVY}14`, color: NAVY }}><Building2 className="h-6 w-6" /></div>
        <div className="min-w-0">
          <h2 className="text-xl font-bold" style={{ color: NAVY }}>{b.branch} Şubesi — Detaylı Görünüm</h2>
          <p className="text-xs text-muted-foreground">{b.target_basis}</p>
        </div>
        {b.winning ? (
          <span className="ml-auto text-sm font-bold px-3 py-1 rounded-full" style={{ background: "#16A34A1a", color: "#16A34A" }} data-testid="branch-detail-status">Hedef Tuttu ✓</span>
        ) : (
          <span className="ml-auto text-sm font-bold px-3 py-1 rounded-full" style={{ background: "#DC26261a", color: "#DC2626" }} data-testid="branch-detail-status">{b.remaining} oy gerek</span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpi.map((k) => (
          <div key={k.label} className="rounded-xl border border-border p-3 text-center">
            <p className="text-2xl font-bold" style={{ color: k.color }}>{k.val}</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-semibold mb-2" style={{ color: NAVY }}>Oy Eğilimi Dağılımı (FAAL üyeler)</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={predData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="k" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={54} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="v" name="Firma" radius={[6, 6, 0, 0]}>
                  {predData.map((d) => <Cell key={d.k} fill={d.c} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <p className="text-sm font-semibold mb-2" style={{ color: NAVY }}>Hedef ve Tahmin (oy)</p>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={targetData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                <XAxis dataKey="k" tick={{ fontSize: 10 }} interval={0} angle={-15} textAnchor="end" height={54} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="v" name="Oy" radius={[6, 6, 0, 0]}>
                  {targetData.map((d) => <Cell key={d.k} fill={d.c} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: NAVY }}>Katılım Trendi (%)</p>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%" minWidth={0}>
            <BarChart data={partData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="k" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
              <Tooltip formatter={(val) => `%${val}`} />
              <Bar dataKey="v" name="Katılım" fill="#0EA5E9" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {(b.team || []).length > 0 && (
        <div>
          <p className="text-sm font-semibold mb-2" style={{ color: NAVY }}>Sorumlu Performansı</p>
          <div className="rounded-lg border border-border overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid="branch-detail-team">
              <thead><tr className="bg-muted/60 text-left text-xs uppercase text-muted-foreground">
                <dtT.Th sortKey="dt-sorumlu" get={(r) => r.name} text className="px-3 py-2 font-medium">Sorumlu</dtT.Th>
                <dtT.Th sortKey="dt-atanan" get={(r) => r.assigned} className="px-3 py-2 font-medium">Atanan</dtT.Th>
                <dtT.Th sortKey="dt-gorusulen" get={(r) => r.contacted} className="px-3 py-2 font-medium">Görüşülen</dtT.Th>
                <dtT.Th sortKey="dt-kesin" get={(r) => r.kesin} className="px-3 py-2 font-medium">Kesin</dtT.Th>
              </tr></thead>
              <tbody>
                {dtT.sorted.map((t) => (
                  <tr key={t.name} className="border-t border-border/60">
                    <td className="px-3 py-2 font-medium">{t.name}</td>
                    <td className="px-3 py-2">{t.assigned}</td>
                    <td className="px-3 py-2">{t.contacted}</td>
                    <td className="px-3 py-2 font-semibold text-emerald-600">{t.kesin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function BranchPanel() {
  const { user } = useAuth();
  const canSeeConflicts = !!user && user.role === "super_admin";
  const [data, setData] = useState(null);
  const [branch, setBranch] = useState("all");
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [elections, setElections] = useState({});
  const [view, setView] = useState("panel");

  const load = (b) => {
    setLoading(true);
    api.get("/dashboard/branch-panel", { params: { branch: b && b !== "all" ? b : undefined } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Şube paneli yüklenemedi"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load("all"); }, []);
  useEffect(() => { api.get("/dashboard/branch-elections").then(({ data }) => setElections(data)).catch(() => {}); }, []);
  const onSavedEl = (br, el) => { setElections((m) => ({ ...m, [br]: el })); load(branch); };

  const onBranchChange = (v) => { setBranch(v); load(v); };

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const order = await exportOrder((data?.rows || []).map((r) => r.branch));
      const res = await api.get("/dashboard/branch-panel/export", {
        params: { branch: branch !== "all" ? branch : undefined, format: fmt, order },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sube_paneli.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Dışa aktarım başarısız");
    } finally {
      setExporting(false);
    }
  };

  const branches = data?.branches || [];

  return (
    <div className="space-y-6" data-testid="branch-panel-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: `${NAVY}1a`, color: NAVY }}>
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#0F2C59] dark:text-sky-300">Şube Paneli</h1>
            <p className="text-sm text-muted-foreground">Merkezden bağımsız şube seçimi — sadece şube üyeleri üzerinden hedef ve saha takibi</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.can_select && (
            <Select value={branch} onValueChange={onBranchChange}>
              <SelectTrigger className="w-full sm:w-[220px]" data-testid="branch-panel-select"><SelectValue placeholder="Şube" /></SelectTrigger>
              <SelectContent className="max-h-[320px]">
                <SelectItem value="all">Tüm Şubeler</SelectItem>
                {(data?.available_branches || []).map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" disabled={exporting || branches.length === 0} onClick={() => doExport("xlsx")} data-testid="branch-panel-export-xlsx">
            <Download className="h-4 w-4 mr-2" />Excel
          </Button>
          <Button variant="outline" size="sm" disabled={exporting || branches.length === 0} onClick={() => doExport("pdf")} data-testid="branch-panel-export-pdf">
            <FileText className="h-4 w-4 mr-2" />PDF
          </Button>
        </div>
      </div>

      {(() => {
        const ab = branch !== "all" ? branch : (branches.length === 1 ? branches[0].branch : null);
        if (!ab) return null;
        return <ElectionCountdown dateStr={BRANCH_ELECTION_DATES[ab] || null} title={`${ab} Şubesi Seçimi`} />;
      })()}

      {canSeeConflicts && <BranchConflicts branch={branch} />}

      <div className="inline-flex rounded-lg border border-border overflow-hidden" data-testid="bp-view-toggle">
        <button type="button" onClick={() => setView("panel")}
          className={`px-3 py-1.5 text-xs font-medium ${view === "panel" ? "bg-[#0F2C59] text-white" : "hover:bg-muted"}`} data-testid="bp-view-panel">Şube Özetleri</button>
        <button type="button" onClick={() => setView("voters")}
          className={`px-3 py-1.5 text-xs font-medium ${view === "voters" ? "bg-violet-600 text-white" : "hover:bg-muted"}`} data-testid="bp-view-voters">Şube Seçmen Listesi</button>
      </div>

      {view === "voters" ? (
        <BranchVoters branch={branch} onBranchChange={onBranchChange} />
      ) : loading ? (
        <Skeleton className="h-96 w-full" />
      ) : branches.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="branch-panel-empty">
          Bu şube için üye firma bulunamadı.
        </Card>
      ) : (
        <div className="space-y-6">
          {branches.length >= 2 && <BranchCountCheck branches={branches} />}
          {branch !== "all" && branches.length === 1 && <BranchDetail b={branches[0]} />}
          {branches.length >= 2 && <BranchCompare branches={branches} />}
          {branches.map((b) => <BranchCard key={b.branch} b={b} elSeed={elections[b.branch]} onSavedEl={onSavedEl} />)}
        </div>
      )}
    </div>
  );
}
